import {
  SchemaItemProps,
  View,
  useAppContext,
  useClientContext,
  useField,
} from "@data-root/react";
import { SchemaItem } from "@data-root/types";
import {
  FC,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Space,
  App as AntdApp,
  Popover,
  Typography,
  Button,
  Skeleton,
} from "antd";
import {
  CameraOutlined,
  LoadingOutlined,
  OpenAIOutlined,
  SoundOutlined,
} from "@ant-design/icons";
import { useAudioRecorder } from "../hooks/useAudioRecorder";
import type {
  GenerationRecord,
  TranscriptionRecord,
} from "../../api/_pocketbase";

const { Paragraph, Text } = Typography;

const Wrapper: FC<PropsWithChildren<SchemaItemProps>> = ({
  children,
  item,
}) => {
  const antdApp = AntdApp.useApp();
  const { core } = useClientContext();
  const { appId, tableId } = useAppContext();
  const { setValue, value, loaded, editable } = useField(item);

  const [transcription, setTranscription] = useState<string | null>(null);
  const [triedLongTranscription, setTriedLongTranscription] = useState(false);
  const [transcriptionOpen, setTranscriptionOpen] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);

  const [generation, setGeneration] = useState<string | null>(null);
  const [generationOpen, setGenerationOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  const {
    startRecording,
    stopRecording,
    togglePauseResume,
    recordingBlobRef,
    isRecording,
    isPaused,
    recordingTime,
  } = useAudioRecorder({});

  useEffect(() => {
    if (recordingTime >= 60 && isRecording) {
      setTriedLongTranscription(true);
      stopRecording();

      antdApp.notification.info({
        message: "Grabación detenida",
        description:
          "Se ha detenido la grabación automáticamente. Para grabar más tiempo, por favor contacte a ventas@dataroot.cl",
      });
    }
  }, [antdApp.notification, isRecording, recordingTime, stopRecording]);

  const onTranscribe = useCallback(async () => {
    const { token } = core;

    const url = window.location.href;
    const apiUrl = new URL("api/ai/transcription", url);

    const form = new FormData();
    form.append("who", core.user!.id);
    form.append("trace", `${appId}/${tableId}/${item.id}`);
    form.append("appId", appId);
    form.append("deploymentId", "dev");
    form.append("triedLongTranscription", triedLongTranscription.toString());
    form.append("audio", recordingBlobRef.current);

    try {
      setIsTranscribing(true);

      const response = await fetch(apiUrl.href, {
        method: "POST",
        body: form,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = (await response.json()) as TranscriptionRecord;
        // setValue(data.text, data.text);
        setTranscription(data.text);
        setTranscriptionOpen(true);
        setTriedLongTranscription(false);
      } else if (response.status === 429) {
        antdApp.notification.error({
          message: "Cuota excedida",
          description:
            "Si desea continuar utilizando esta funcionalidad, por favor contacte a ventas@dataroot.cl",
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsTranscribing(false);
    }
  }, [
    antdApp.notification,
    appId,
    core,
    item.id,
    recordingBlobRef,
    tableId,
    triedLongTranscription,
  ]);

  const onStopRecording = useCallback(() => {
    stopRecording();
    onTranscribe();
    setTranscriptionOpen(true);
  }, [onTranscribe, stopRecording]);

  const onRefine = useCallback(async () => {
    const { token } = core;

    const url = window.location.href;
    const apiUrl = new URL("api/ai/generation", url);
    try {
      setIsGenerating(true);

      const response = await fetch(apiUrl.href, {
        method: "POST",
        body: JSON.stringify({
          who: core.user!.id,
          trace: `${appId}/${tableId}/${item.id}`,

          appId,
          deploymentId: "dev",

          taskId: "generate_recommendation",
          text: value,
        }),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = (await response.json()) as GenerationRecord;
        setGeneration(data.generated);
        setGenerationOpen(true);
      } else if (response.status === 429) {
        antdApp.notification.error({
          message: "Cuota excedida",
          description:
            "Si desea continuar utilizando esta funcionalidad, por favor contacte a ventas@dataroot.cl",
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsGenerating(false);
    }
  }, [antdApp.notification, appId, core, item.id, tableId, value]);

  const appendValue = useCallback(
    (text: string) => {
      const newText = `${value ?? ""}\n\n${text}`;
      setValue(newText, newText, { evaluation: true });
    },
    [setValue, value]
  );

  const transcriptionContent = useMemo(
    () => (
      <Space direction="vertical">
        {isTranscribing ? (
          <Skeleton active />
        ) : (
          <Paragraph>{transcription}</Paragraph>
        )}

        <Space>
          <Button
            onClick={() => {
              setTranscriptionOpen(false);
              setTranscription(null);
            }}
          >
            Descartar
          </Button>

          <Button
            onClick={() => {
              appendValue(transcription);
              setTranscriptionOpen(false);
            }}
          >
            Agregar al final
          </Button>

          <Button
            type="primary"
            onClick={() => {
              setValue(transcription, transcription, { evaluation: true });
              setTranscriptionOpen(false);
            }}
          >
            Reemplazar
          </Button>
        </Space>
      </Space>
    ),
    [appendValue, isTranscribing, setValue, transcription]
  );

  const generationContent = useMemo(
    () => (
      <Space direction="vertical">
        <Paragraph>{generation}</Paragraph>

        <Space>
          <Button
            onClick={() => {
              setGenerationOpen(false);
              setGeneration(null);
            }}
          >
            Descartar
          </Button>

          <Button
            onClick={() => {
              appendValue(generation);
              setGenerationOpen(false);
            }}
          >
            Agregar al final
          </Button>
          <Button
            type="primary"
            onClick={() => {
              setValue(generation, generation, { evaluation: true });
              setGenerationOpen(false);
            }}
          >
            Reemplazar
          </Button>
        </Space>
      </Space>
    ),
    [appendValue, generation, setValue]
  );

  return (
    <View
      style={{
        backgroundColor: "#eeeeee",
        padding: 10,
        width: "100%",
      }}
    >
      <Space>
        <Popover
          open={transcriptionOpen}
          title="Transcripción"
          content={transcriptionContent}
        >
          <Button
            type={isRecording || isPaused ? "primary" : "default"}
            icon={isRecording ? <LoadingOutlined spin /> : <SoundOutlined />}
            onClick={
              isRecording
                ? onStopRecording
                : isPaused
                  ? togglePauseResume
                  : startRecording
            }
            disabled={!editable || !loaded}
          >
            {isRecording ? "Grabando" : isPaused ? "Pausado" : "Grabar"}
          </Button>
        </Popover>

        <Popover
          open={generationOpen}
          title="Generación"
          content={generationContent}
        >
          <Button
            icon={<OpenAIOutlined />}
            onClick={onRefine}
            disabled={isRecording || isPaused || !value || !editable || !loaded}
            loading={isGenerating}
          >
            Refinar
          </Button>
        </Popover>

        <Popover
          trigger="click"
          title={
            <Paragraph>
              Describe o transcribe imagenes con <Text strong>DataRoot AI</Text>
            </Paragraph>
          }
          content={
            <Paragraph>
              Contacte a{" "}
              <Text code copyable>
                felipe@dataroot.cl
              </Text>{" "}
              para activar esta funcionalidad.
            </Paragraph>
          }
        >
          <Button type="dashed" icon={<CameraOutlined />}>
            Imagen a texto
          </Button>
        </Popover>
      </Space>

      {children}
    </View>
  );
};

export const aiGuard =
  <T extends SchemaItem>(
    Component: FC<SchemaItemProps<T>>
  ): FC<SchemaItemProps<T>> =>
  (props) => {
    const ai = useMemo(() => props.item.ai ?? false, [props.item.ai]);

    const element = useMemo(() => <Component {...props} />, [props]);

    return ai ? <Wrapper {...props}>{element}</Wrapper> : element;
  };
